import {
  AlertTriangle,
  CircleSlash,
  ClipboardCheck,
  FilePlus2,
  Info,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  ShieldPlus,
  Wand2,
  Wrench,
} from "lucide-react";
import { type FormEvent, useCallback, useId, useMemo, useState } from "react";
import { ApiError, api } from "../../api/client.js";
import type {
  PatchVerificationReceipt,
  RemediationApplyMode,
  RemediationProposal,
  RemediationProposalStatus,
  RemediationRoe,
} from "../../api/types.js";
import { findingRecordHref } from "../../app/routing.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { Badge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { formatDateTime, shortHash, titleCase } from "../../lib/format.js";
import {
  countProposalBuckets,
  filterProposals,
  proposalBucket,
  remediationHeadline,
  type RemediationQueueBucket,
} from "./remediation-queue.js";
import { RemediationRoePosture } from "./RemediationRoePosture.js";
import "./remediation-queue.css";

type RemediationAction =
  | "preview"
  | "approve"
  | "reject"
  | "rollback"
  | "expire"
  | "generate"
  | "verify";

const STATUS_TONE: Record<
  RemediationProposalStatus,
  "neutral" | "info" | "positive" | "critical" | "muted"
> = {
  draft: "neutral",
  previewed: "info",
  generating: "info",
  verifying: "info",
  ready: "positive",
  approved: "positive",
  rejected: "critical",
  rolled_back: "muted",
  expired: "muted",
};

function custodyRef(id: string, length = 14): string {
  return shortHash(id.replace(/^(finding|repo|remediation)_/u, ""), length);
}

/** Mirrors `canTransitionRemediation` (packages/domain/src/remediation.ts) --
 * only the edges that state machine allows are ever offered as buttons, so
 * an operator can never even attempt an invalid transition from this page.
 * `generating`/`verifying`/`ready` are the auto-generation path (Phase 2+);
 * no phase before Phase 3+ drives a proposal into them from this UI, but a
 * proposal can already reach them via the `/generate` API route directly, so
 * this page must still render their legal human actions (reject/expire, and
 * approve once `ready`) rather than silently treating them as terminal. */
function actionsFor(status: RemediationProposalStatus): RemediationAction[] {
  switch (status) {
    case "draft":
      return ["generate", "preview", "reject", "expire"];
    case "previewed":
      return ["approve", "reject", "expire"];
    case "generating":
      return ["reject", "expire"];
    case "verifying":
      return ["verify", "reject", "expire"];
    case "ready":
      return ["approve", "reject", "expire"];
    case "approved":
      return ["rollback", "expire"];
    default:
      return [];
  }
}

const ACTION_LABEL: Record<RemediationAction, string> = {
  preview: "Mark previewed",
  approve: "Approve",
  reject: "Reject",
  rollback: "Roll back",
  expire: "Expire",
  generate: "Generate fix",
  verify: "Verify fix",
};

const REMEDIATION_EXAMPLES = [
  {
    risk: "Dynamic code evaluation",
    before: "callback.call(this, currentElement)",
    after: "Replace string/function ambiguity with an allowlisted handler map.",
    proof: "Replay the cited sink and verify no dynamic execution path remains.",
  },
  {
    risk: "SQL injection",
    before: "query + request.args.id",
    after: "Bind request values through parameters instead of string concatenation.",
    proof: "Re-scan the frozen patch preview for the exact query sink.",
  },
  {
    risk: "Hard-coded credential",
    before: "API key committed in source history",
    after:
      "Rotate the credential, replace source use with secret-manager lookup, and retain the fingerprint.",
    proof: "Confirm the raw secret is absent while the redacted evidence remains auditable.",
  },
  {
    risk: "Classical-only key exchange",
    before: "RSA/ECDH-only negotiation",
    after: "Plan hybrid or standardized PQC migration after owner confirms retention horizon.",
    proof: "CBOM row links the crypto use, role, and migration dependency.",
  },
  {
    risk: "Over-broad authorization",
    before: "Admin path reachable without a scoped permission check",
    after: "Move authorization to the entry boundary and preserve deny-by-default behavior.",
    proof: "Reviewer records a governed 4V decision, then verifies a new scan.",
  },
];

/**
 * Whether `runAction`'s success path should clear the "Rationale" input after
 * `action` completes. `generate` and `verify` hit routes that read no request
 * body at all -- the rationale the operator typed is never sent or recorded
 * anywhere, so clearing it would silently discard their input with no
 * indication it was lost. Every other action sends `rationale` to the API,
 * so it is safe (and expected) to clear the field once it has been recorded.
 */
export function shouldClearRationaleAfter(action: RemediationAction): boolean {
  return action !== "generate" && action !== "verify";
}

/**
 * Whether the "Authorize repository" form should be offered below the ROE
 * lookup. Only once a repository has actually been looked up, the lookup has
 * settled (not loading, not errored), and it came back with no active
 * authorization -- an existing authorization already has its own posture and
 * revoke control, and offering to create a second one over it (or over an
 * unresolved error) would be confusing.
 */
export function shouldOfferRoeCreation(
  hasQueried: boolean,
  isLoading: boolean,
  hasError: boolean,
  roe: RemediationRoe | null,
): boolean {
  return hasQueried && !isLoading && !hasError && roe === null;
}

function RemediationExamples() {
  return (
    <section className="remediation-examples" aria-labelledby="remediation-examples-heading">
      <div>
        <span className="page-kicker">Five proof-backed fix stories</span>
        <h2 id="remediation-examples-heading">A fix is not “done” until the evidence changes.</h2>
        <p>
          Use these examples as the demo map: each starts from cited evidence, proposes a safe
          change, and names the verification proof needed before approval.
        </p>
      </div>
      <div className="remediation-example-grid">
        {REMEDIATION_EXAMPLES.map((example, index) => (
          <article key={example.risk}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{example.risk}</h3>
            <dl>
              <div>
                <dt>Before</dt>
                <dd>{example.before}</dd>
              </div>
              <div>
                <dt>Plan</dt>
                <dd>{example.after}</dd>
              </div>
              <div>
                <dt>Proof</dt>
                <dd>{example.proof}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ProposalCard({
  proposal,
  onTransitioned,
  canWrite = true,
  canApprove = false,
}: {
  proposal: RemediationProposal;
  onTransitioned: (updated: RemediationProposal) => void;
  canWrite?: boolean | undefined;
  canApprove?: boolean | undefined;
}) {
  const rationaleId = useId();
  const [rationale, setRationale] = useState("");
  const [busyAction, setBusyAction] = useState<RemediationAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const approvalActions: readonly RemediationAction[] = ["approve", "reject", "rollback", "expire"];
  const available = actionsFor(proposal.status).filter((action) =>
    approvalActions.includes(action) ? canApprove : canWrite,
  );

  const runAction = async (action: RemediationAction) => {
    setBusyAction(action);
    setError(null);
    try {
      const updated =
        action === "generate"
          ? await api.generateRemediationProposal(proposal.id)
          : action === "verify"
            ? await api.verifyRemediationProposal(proposal.id)
            : await api.transitionRemediationProposal(
                proposal.id,
                action,
                rationale.trim() || undefined,
              );
      if (shouldClearRationaleAfter(action)) {
        setRationale("");
      }
      onTransitioned(updated);
    } catch (reason) {
      setError(
        reason instanceof ApiError ? reason.message : "The transition could not be recorded.",
      );
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <article
      className="page-section remediation-proposal-card"
      aria-label={`Remediation proposal ${proposal.id}`}
    >
      <div className="inspector-section__heading">
        <Wrench aria-hidden="true" size={16} />
        <h3>{proposal.summary}</h3>
        <Badge label={titleCase(proposal.status)} tone={STATUS_TONE[proposal.status]} />
      </div>
      <dl className="detail-grid remediation-proposal-meta">
        <div>
          <dt>Review state</dt>
          <dd>{titleCase(proposal.status)}</dd>
        </div>
        <div>
          <dt>Created by</dt>
          <dd>{proposal.createdBy}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{formatDateTime(proposal.expiresAt)}</dd>
        </div>
      </dl>
      <VerificationEvidence receipt={proposal.verification ?? null} />
      <details className="menu-popover remediation-custody-detail">
        <summary className="control-button">Show proposal custody ID</summary>
        <dl className="detail-grid">
          <div>
            <dt>Proposal</dt>
            <dd>
              <code>{custodyRef(proposal.id, 12)}</code>
            </dd>
          </div>
          <div>
            <dt>Approved by</dt>
            <dd>{proposal.approvedBy ?? "—"}</dd>
          </div>
        </dl>
      </details>
      <details className="menu-popover">
        <summary className="control-button">Preview diff (never applied to source)</summary>
        <pre className="code-evidence">
          <code>{proposal.previewDiff}</code>
        </pre>
      </details>
      <p className="muted-copy">
        <strong>Rollback plan:</strong> {proposal.rollbackPlan}
      </p>
      {proposal.status === "rejected" ? (
        <p className="muted-copy">
          This proposal was rejected. See the Audit trail for finding{" "}
          <code>{proposal.findingId}</code> for the rationale.
        </p>
      ) : null}
      {available.length > 0 ? (
        <form
          className="delivery-form"
          onSubmit={(event) => event.preventDefault()}
          aria-label="Remediation transition"
        >
          <label className="form-field" htmlFor={rationaleId}>
            <span>Rationale (optional)</span>
            <input
              id={rationaleId}
              value={rationale}
              maxLength={4000}
              placeholder="State the reviewer basis for this transition."
              onChange={(event) => setRationale(event.target.value)}
            />
          </label>
          <div className="human-review-form__footer">
            {available.map((action) => (
              <button
                className="button"
                type="button"
                key={action}
                disabled={busyAction !== null}
                onClick={() => void runAction(action)}
              >
                {action === "rollback" ? (
                  <RotateCcw aria-hidden="true" size={14} />
                ) : action === "reject" || action === "expire" ? (
                  <CircleSlash aria-hidden="true" size={14} />
                ) : action === "generate" ? (
                  <Wand2 aria-hidden="true" size={14} />
                ) : action === "verify" ? (
                  <ClipboardCheck aria-hidden="true" size={14} />
                ) : (
                  <ShieldCheck aria-hidden="true" size={14} />
                )}
                {busyAction === action ? "Recording…" : ACTION_LABEL[action]}
              </button>
            ))}
          </div>
        </form>
      ) : !canWrite ? (
        <p className="muted-copy">
          Your current session can inspect this proposal but cannot transition it.
        </p>
      ) : (
        <p className="muted-copy">
          This proposal is in a terminal state; no further transitions apply.
        </p>
      )}
      {error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
    </article>
  );
}

const VERIFICATION_LABEL: Record<PatchVerificationReceipt["status"], string> = {
  verified: "Verified",
  partially_verified: "Partially verified",
  unverified_requires_application_tests: "Unverified — application tests required",
  rejected: "Rejected",
};

export function VerificationEvidence({ receipt }: { receipt: PatchVerificationReceipt | null }) {
  if (!receipt) {
    return (
      <section
        className="page-section remediation-verification-card"
        aria-label="Verification evidence"
      >
        <h4>Verification evidence</h4>
        <p className="muted-copy">Not evaluated. No immutable verification receipt exists.</p>
      </section>
    );
  }
  const tone =
    receipt.status === "verified"
      ? "positive"
      : receipt.status === "rejected"
        ? "critical"
        : "info";
  return (
    <section
      className="page-section remediation-verification-card"
      aria-label="Verification evidence"
    >
      <div className="inspector-section__heading">
        <h4>Verification evidence</h4>
        <Badge label={VERIFICATION_LABEL[receipt.status]} tone={tone} />
      </div>
      <div className="remediation-verification-grid">
        <article>
          <span>Evidence changed</span>
          <strong>
            {receipt.originalFindingCount} → {receipt.rescannedFindingCount}
          </strong>
          <p>Finding count before and after the static verification pass.</p>
        </article>
        <article>
          <span>Detector result</span>
          <strong>{titleCase(receipt.rescanResult)}</strong>
          <p>Producing-detector rescan was re-run against the frozen patch preview.</p>
        </article>
        <article>
          <span>Independent checks</span>
          <strong>{receipt.independentRuleChecks}</strong>
          <p>Additional static checks reviewed the same remediation path.</p>
        </article>
      </div>
      <details className="menu-popover remediation-custody-detail">
        <summary className="control-button">Show verification custody and limitations</summary>
        <dl className="detail-grid">
          <div>
            <dt>Receipt</dt>
            <dd>
              <code>{shortHash(receipt.receiptSha256, 12)}</code>
            </dd>
          </div>
          <div>
            <dt>Source before</dt>
            <dd>
              <code>{shortHash(receipt.beforeFileSha256, 12)}</code>
            </dd>
          </div>
          <div>
            <dt>Source after</dt>
            <dd>
              <code>{shortHash(receipt.afterFileSha256, 12)}</code>
            </dd>
          </div>
          <div>
            <dt>Focused application tests</dt>
            <dd>{receipt.focusedTestChecks}</dd>
          </div>
        </dl>
        {receipt.limitations.length > 0 ? (
          <div>
            <strong>Limitations</strong>
            <ul>
              {receipt.limitations.map((limitation) => (
                <li key={limitation}>{limitation}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="muted-copy">No receipt limitations were recorded.</p>
        )}
      </details>
    </section>
  );
}

function CreateProposalForm({
  findingId,
  onCreated,
}: {
  findingId: string;
  onCreated: (proposal: RemediationProposal) => void;
}) {
  const summaryId = useId();
  const previewDiffId = useId();
  const rollbackPlanId = useId();
  const [summary, setSummary] = useState("");
  const [previewDiff, setPreviewDiff] = useState("");
  const [rollbackPlan, setRollbackPlan] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const proposal = await api.createRemediationProposal(findingId, {
        summary: summary.trim(),
        previewDiff,
        rollbackPlan: rollbackPlan.trim(),
      });
      setSummary("");
      setPreviewDiff("");
      setRollbackPlan("");
      onCreated(proposal);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "The proposal could not be drafted.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="page-section" aria-labelledby="create-proposal-heading">
      <h2 id="create-proposal-heading">Draft a remediation proposal</h2>
      <p>
        A proposal is a reviewable, previewable text plan -- <code>previewDiff</code> is stored for
        operator review only. In this PoC, the platform records the plan and decision trail without
        applying changes to scanned source.
      </p>
      <form className="delivery-form" onSubmit={(event) => void submit(event)}>
        <label className="form-field" htmlFor={summaryId}>
          <span>Summary</span>
          <input
            id={summaryId}
            required
            minLength={3}
            maxLength={4000}
            value={summary}
            placeholder="e.g. Parameterize the SQL query in db.execute(...)"
            onChange={(event) => setSummary(event.target.value)}
          />
        </label>
        <label className="form-field" htmlFor={previewDiffId}>
          <span>Preview diff</span>
          <textarea
            id={previewDiffId}
            required
            rows={5}
            minLength={1}
            maxLength={200_000}
            value={previewDiff}
            placeholder={[
              "--- a/segment-01/hardcoded-credentials/case_003.ts",
              "+++ b/segment-01/hardcoded-credentials/case_003.ts",
              '-const token = "redacted-static-secret";',
              '+const token = readRequiredSecret("SERVICE_TOKEN");',
            ].join("\n")}
            onChange={(event) => setPreviewDiff(event.target.value)}
          />
        </label>
        <label className="form-field" htmlFor={rollbackPlanId}>
          <span>Rollback plan</span>
          <input
            id={rollbackPlanId}
            required
            minLength={3}
            maxLength={4000}
            value={rollbackPlan}
            placeholder="e.g. Revert this commit if safe() regresses behavior."
            onChange={(event) => setRollbackPlan(event.target.value)}
          />
        </label>
        <button className="button button--primary" type="submit" disabled={submitting}>
          <FilePlus2 aria-hidden="true" size={15} />
          {submitting ? "Drafting…" : "Draft proposal"}
        </button>
      </form>
      {error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
    </section>
  );
}

const APPLY_MODE_OPTIONS: ReadonlyArray<{
  value: RemediationApplyMode;
  label: string;
  enabled: boolean;
}> = [{ value: "view-only", label: "View only", enabled: true }];

function CreateRoeForm({
  repositoryId,
  onCreated,
}: {
  repositoryId: string;
  onCreated: (roe: RemediationRoe) => void;
}) {
  const applyModeName = useId();
  const autoApproveId = useId();
  const [applyMode, setApplyMode] = useState<RemediationApplyMode>("view-only");
  const [autoApproveVerifiedFixes, setAutoApproveVerifiedFixes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const roe = await api.createRemediationRoe(repositoryId, {
        applyMode,
        autoApproveVerifiedFixes,
      });
      onCreated(roe);
    } catch (reason) {
      setError(
        reason instanceof ApiError ? reason.message : "The authorization could not be created.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="page-section" aria-labelledby="create-roe-heading">
      <h2 id="create-roe-heading">Authorize this repository</h2>
      <p>
        No active authorization is on file, so remediation stays view only. This PoC records a
        governed plan, preview, approval, and rollback trail without mutating scanned source.
      </p>
      <form className="delivery-form remediation-roe-form" onSubmit={(event) => void submit(event)}>
        <fieldset className="remediation-apply-grid">
          <legend>Apply mode</legend>
          {APPLY_MODE_OPTIONS.map((option) => (
            <label key={option.value} className="remediation-option">
              <input
                type="radio"
                name={applyModeName}
                value={option.value}
                checked={applyMode === option.value}
                disabled={!option.enabled}
                onChange={() => setApplyMode(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
        <label className="remediation-checkbox" htmlFor={autoApproveId}>
          <input
            id={autoApproveId}
            type="checkbox"
            checked={autoApproveVerifiedFixes}
            onChange={(event) => setAutoApproveVerifiedFixes(event.target.checked)}
          />
          <span>Auto-approve verified fixes</span>
        </label>
        <button className="button button--primary" type="submit" disabled={submitting}>
          <ShieldPlus aria-hidden="true" size={15} />
          {submitting ? "Authorizing…" : "Authorize repository"}
        </button>
      </form>
      {error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <AlertTriangle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
    </section>
  );
}

export function RemediationPage({
  initialFindingId,
  initialRepositoryId,
  canWrite = true,
  canApprove = false,
}: {
  initialFindingId?: string | undefined;
  initialRepositoryId?: string | undefined;
  canWrite?: boolean | undefined;
  canApprove?: boolean | undefined;
} = {}) {
  const findingIdFieldId = useId();
  const [findingId, setFindingId] = useState(initialFindingId ?? "");
  const [queriedFindingId, setQueriedFindingId] = useState<string | null>(initialFindingId ?? null);
  const [bucket, setBucket] = useState<RemediationQueueBucket | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const loadProposals = useCallback(
    (signal: AbortSignal) => api.listRemediationProposals({}, signal),
    [],
  );
  const proposals = useApiResource(loadProposals, 15_000);
  const items = proposals.data?.items ?? [];
  const counts = countProposalBuckets(items);
  const visibleItems = useMemo(
    () => filterProposals(items, bucket, queriedFindingId),
    [bucket, items, queriedFindingId],
  );
  const selected = visibleItems.find((item) => item.id === selectedId) ?? visibleItems[0] ?? null;

  const submitLookup = (event: FormEvent) => {
    event.preventDefault();
    setQueriedFindingId(findingId.trim() || null);
  };

  const repositoryIdFieldId = useId();
  const [repositoryId, setRepositoryId] = useState(initialRepositoryId ?? "");
  const [queriedRepositoryId, setQueriedRepositoryId] = useState<string | null>(
    initialRepositoryId ?? null,
  );
  const loadRoe = useCallback(
    (signal: AbortSignal) =>
      queriedRepositoryId
        ? api.getRemediationRoe(queriedRepositoryId, signal)
        : Promise.resolve(null),
    [queriedRepositoryId],
  );
  const roe = useApiResource(loadRoe, 15_000);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const submitRepositoryLookup = (event: FormEvent) => {
    event.preventDefault();
    setRevokeError(null);
    setQueriedRepositoryId(repositoryId.trim() || null);
  };
  const hasInitialRepositorySelection = Boolean(
    initialRepositoryId && repositoryId === initialRepositoryId,
  );
  const hasInitialFindingSelection = Boolean(initialFindingId && findingId === initialFindingId);

  const revokeAndRefresh = async () => {
    if (!queriedRepositoryId) return;
    setRevokeError(null);
    try {
      await api.revokeRemediationRoe(queriedRepositoryId);
      roe.refresh();
    } catch (reason) {
      setRevokeError(
        reason instanceof ApiError ? reason.message : "The authorization could not be revoked.",
      );
    }
  };

  return (
    <section className="page remediation-page" aria-labelledby="remediation-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Guided remediation</span>
          <h1 id="remediation-heading">Remediation</h1>
          <p>
            Turn a verified risk into a reviewable plan, without changing the source you scanned.
          </p>
        </div>
        <div className="page-heading__actions">
          <button
            className="icon-button"
            type="button"
            aria-label="Refresh remediation proposals"
            title="Refresh remediation proposals"
            disabled={proposals.isRefreshing}
            onClick={proposals.refresh}
          >
            <RefreshCw
              aria-hidden="true"
              className={proposals.isRefreshing ? "spin" : undefined}
              size={17}
            />
          </button>
        </div>
      </div>

      <div className="inline-alert" role="status">
        <Info aria-hidden="true" size={16} />
        <span>
          Remediation never mutates scanned source. Every proposal, preview, approval, rejection,
          and rollback is an append-only, audited decision record.
        </span>
      </div>

      <section className="evidence-decision-hero" aria-label="Remediation queue summary">
        <div>
          <span className="page-kicker">This queue</span>
          <h2>A plan is not done until evidence changes.</h2>
          <p>{remediationHeadline(counts)}</p>
        </div>
        <dl className="evidence-hero-metrics">
          <div data-metric="open">
            <dt>Open</dt>
            <dd>{counts.open}</dd>
          </div>
          <div data-metric="approved">
            <dt>Approved</dt>
            <dd>{counts.approved}</dd>
          </div>
          <div data-metric="closed">
            <dt>Closed</dt>
            <dd>{counts.closed}</dd>
          </div>
        </dl>
      </section>

      <fieldset className="remediation-bucket-chips">
        <legend className="sr-only">Proposal status</legend>
        {(
          [
            ["all", "All"],
            ["open", "Open"],
            ["approved", "Approved"],
            ["closed", "Closed"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={bucket === value ? "is-on" : ""}
            onClick={() => setBucket(value)}
          >
            {label}
          </button>
        ))}
      </fieldset>

      {proposals.error && proposals.data ? (
        <InlineError error={proposals.error} onRetry={proposals.refresh} />
      ) : null}
      {proposals.isLoading && !proposals.data ? <LoadingTable columns={4} rows={6} /> : null}
      {proposals.error && !proposals.data ? (
        <InlineError error={proposals.error} onRetry={proposals.refresh} />
      ) : null}
      {!proposals.isLoading && proposals.data && visibleItems.length === 0 ? (
        <EmptyState
          title={
            items.length === 0 ? "No remediation proposals yet" : "No proposals in this filter"
          }
          description={
            items.length === 0
              ? "Draft a governed plan from a finding. An empty queue is not a clean estate."
              : "Clear the status or finding filter to see the rest of the queue."
          }
        />
      ) : null}

      {visibleItems.length > 0 ? (
        <section className="remediation-queue" aria-label="Remediation proposals">
          {visibleItems.map((proposal) => (
            <article
              key={proposal.id}
              className={`remediation-queue-row${selected?.id === proposal.id ? " is-selected" : ""}`}
              data-bucket={proposalBucket(proposal.status)}
            >
              <button type="button" onClick={() => setSelectedId(proposal.id)}>
                <strong>{proposal.summary}</strong>
                <small>{proposal.createdBy}</small>
              </button>
              <Badge label={titleCase(proposal.status)} tone={STATUS_TONE[proposal.status]} />
              <a href={findingRecordHref(proposal.findingId)}>Open finding</a>
              <time dateTime={proposal.updatedAt}>{formatDateTime(proposal.updatedAt)}</time>
            </article>
          ))}
        </section>
      ) : null}

      {selected ? (
        <ProposalCard
          proposal={selected}
          canWrite={canWrite}
          canApprove={canApprove}
          onTransitioned={() => proposals.refresh()}
        />
      ) : null}

      <details
        className="governance-detail"
        open={Boolean(initialFindingId || initialRepositoryId)}
      >
        <summary>Draft a plan or check repository authorization</summary>
        <section className="page-section remediation-entry-card" aria-labelledby="roe-heading">
          <span className="page-kicker">Before drafting</span>
          <h2 id="roe-heading">1. Check repository permission</h2>
          <p>Confirm that a repository is authorized for a governed remediation proposal.</p>
          {hasInitialRepositorySelection ? (
            <div className="selected-record-card">
              <div>
                <span>Selected authorized source</span>
                <strong>Repository carried from the finding workflow</strong>
                <details className="inline-technical-id">
                  <summary>View technical custody reference</summary>
                  <code>{custodyRef(repositoryId, 14)}</code>
                </details>
              </div>
              <button
                className="button button--primary"
                type="button"
                onClick={() => setQueriedRepositoryId(repositoryId)}
              >
                Refresh authorization
              </button>
            </div>
          ) : (
            <form className="delivery-form" onSubmit={submitRepositoryLookup}>
              <label className="form-field" htmlFor={repositoryIdFieldId}>
                <span>Repository ID</span>
                <input
                  id={repositoryIdFieldId}
                  required
                  value={repositoryId}
                  placeholder="e.g. repo_1a2b3c…"
                  spellCheck={false}
                  onChange={(event) => setRepositoryId(event.target.value)}
                />
              </label>
              <button className="button button--primary" type="submit">
                Look up authorization
              </button>
            </form>
          )}
          {queriedRepositoryId && !roe.error ? (
            <RemediationRoePosture
              roe={roe.data ?? null}
              canRevoke={canWrite}
              onRevoke={() => {
                void revokeAndRefresh();
              }}
            />
          ) : null}
          {queriedRepositoryId && roe.error ? (
            <InlineError error={roe.error} onRetry={roe.refresh} />
          ) : null}
          {revokeError ? (
            <div className="inline-alert inline-alert--error" role="alert">
              <AlertTriangle aria-hidden="true" size={16} />
              <span>{revokeError}</span>
            </div>
          ) : null}
        </section>

        {queriedRepositoryId &&
        canWrite &&
        shouldOfferRoeCreation(true, roe.isLoading, Boolean(roe.error), roe.data ?? null) ? (
          <CreateRoeForm repositoryId={queriedRepositoryId} onCreated={() => roe.refresh()} />
        ) : null}

        <section className="page-section remediation-entry-card" aria-labelledby="lookup-heading">
          <span className="page-kicker">Open a decision</span>
          <h2 id="lookup-heading">2. Choose a finding</h2>
          <p>
            Filter the queue to one exact finding, or start a new governed plan. Use this only when
            you already have an exact record ID.
          </p>
          {hasInitialFindingSelection ? (
            <div className="selected-record-card">
              <div>
                <span>Selected replay-backed finding</span>
                <strong>Finding carried from the review queue</strong>
                <details className="inline-technical-id">
                  <summary>View technical custody reference</summary>
                  <code>{custodyRef(findingId, 14)}</code>
                </details>
              </div>
              <button
                className="button button--primary"
                type="button"
                onClick={() => setQueriedFindingId(findingId)}
              >
                Filter to this finding
              </button>
            </div>
          ) : (
            <form className="delivery-form" onSubmit={submitLookup}>
              <label className="form-field" htmlFor={findingIdFieldId}>
                <span>Finding ID</span>
                <input
                  id={findingIdFieldId}
                  required
                  value={findingId}
                  placeholder="e.g. finding_1a2b3c…"
                  spellCheck={false}
                  onChange={(event) => setFindingId(event.target.value)}
                />
              </label>
              <button className="button button--primary" type="submit">
                Filter queue
              </button>
            </form>
          )}
        </section>

        {queriedFindingId && canWrite ? (
          <CreateProposalForm findingId={queriedFindingId} onCreated={() => proposals.refresh()} />
        ) : null}
      </details>

      <details className="governance-detail">
        <summary>Example of a proof-backed fix, not this queue</summary>
        <RemediationExamples />
        <section className="remediation-journey" aria-label="Remediation journey">
          <article>
            <span>01</span>
            <h2>Choose the risk</h2>
            <p>Start from a finding with replayable evidence, or enter its ID.</p>
          </article>
          <article>
            <span>02</span>
            <h2>Shape the plan</h2>
            <p>
              Preview the proposed change, expected outcome, and rollback before anyone approves it.
            </p>
          </article>
          <article>
            <span>03</span>
            <h2>Record the decision</h2>
            <p>Keep the approval, rejection, or rollback durable and attributable.</p>
          </article>
        </section>
      </details>
    </section>
  );
}
